import React, { useMemo, useCallback } from 'react';
import moment from 'moment';

import List from 'antd/es/list';
import Divider from 'antd/es/divider';

import { Row, Col } from 'components/layout';
import { Title, Text } from 'components/Text';
import Button from 'components/Button';

// import DaySelect from './DaySelect';
import TimePicker from './TimePicker';
import WeekDayPicker from './WeekDayPicker';

const hourToMoment = (hr) => {
  if (hr === null) return null;
  const m = moment.utc(hr * 60 * 60 * 1000);
  if (!m.isValid()) return null;
  return m;
};

const momentToHours = (m) => {
  return m.hours() + parseFloat((m.minutes() / 60).toFixed(2))
};

const ScheduleShift = ({ shift, onShiftChanged, onShiftRemoved, canBeRemoved }) => {
  const customHours = useMemo(() => {
    return shift.workHours.reduce((customHours, workHour) => {
      if (workHour.workday === null) {
        customHours.push({
          start_hr: workHour.start_hr,
          end_hr: workHour.end_hr,
          days: [],
        });
      } else {
        const index = customHours.findIndex(h => {
          return h.start_hr === workHour.start_hr && h.end_hr === workHour.end_hr;
        });
        if (index !== -1) {
          customHours[index].days.push(workHour.workday);
        } else {
          customHours.push({
            start_hr: workHour.start_hr,
            end_hr: workHour.end_hr,
            days: [workHour.workday],
          });
        }
      }
      return customHours;
    }, []);
  }, [shift.workHours]);

  const { isFull, disabledDays } = useMemo(() => {
    const allDays = Array.from(new Array(7).keys()).map(i => i + 1);
    const usedDays = customHours.reduce((acc, t) => acc.concat(t.days), []);
    return {
      isFull: usedDays.length === allDays.length,
      disabledDays: usedDays,
    };
  }, [customHours]);

  const addCustomTime = useCallback(() => {
    const workHour = shift.workHours[shift.workHours.length - 1];
    if (workHour && workHour.workday === null) {
      return;
    }

    shift.workHours = [...shift.workHours, {
      start_hr: null,
      end_hr: null,
      workday: null,
    }];

    if (typeof onShiftChanged === 'function') {
      onShiftChanged(shift);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift]);

  const updateCustomTime = useCallback((value, index) => {
    const start_hr = momentToHours(value[0]);
    const end_hr = momentToHours(value[1]);
    const hour = customHours[index];
    if (!hour) return;
    
    shift.workHours = shift.workHours.map(h => {
      if (h.start_hr === hour.start_hr &&
        h.end_hr === hour.end_hr
      ) {
        h.start_hr = start_hr;
        h.end_hr = end_hr;
      }
      return h;
    });

    if (typeof onShiftChanged === 'function') {
      onShiftChanged(shift);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift, customHours]);

  const updateCustomTimeDays = useCallback((selectedDays, index) => {
    const hour = customHours[index];
    if (!hour) return;

    const workHours = shift.workHours.filter(h => {
      return !(h.start_hr === hour.start_hr && h.end_hr === hour.end_hr);
    });

    selectedDays.forEach(day => {
      workHours.push({
        start_hr: hour.start_hr,
        end_hr: hour.end_hr,
        workday: day,
      });
    });
    
    shift.workHours = workHours;

    if (typeof onShiftChanged === 'function') {
      onShiftChanged(shift);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift, customHours]);

  const removeCustomTime = useCallback((index) => {
    const hour = customHours[index];
    if (!hour) return;

    shift.workHours = shift.workHours.filter(h => {
      return h.start_hr !== hour.start_hr || h.end_hr !== hour.end_hr;
    });

    if (typeof onShiftChanged === 'function') {
      onShiftChanged(shift);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift, customHours]);

  return (
    <Col span={24}>
      <Row gutter={16}>
        <Col span={7}>
          <Title level={4}>Shift #{shift.shift_name}</Title>
          <Text style={{ display: 'inline-block' }}>
            Set start time and end time for the shift
          </Text>
          {canBeRemoved !== false && (
            <Button
              type="link"
              icon={{ name: "trash-2", size: 18, strokeWidth: "1" }}
              style={{
                color: '#F5222D',
                paddingLeft: 0,
                marginTop: '10%',
              }}
              onClick={onShiftRemoved}
            >
              Remove Shift
            </Button>
          )}
        </Col>
        <Col span={17}>
          <List
            dataSource={customHours || []}
            pagination={false}
            split={true}
            style={{ marginTop: '-12px' }}
            renderItem={(hour, i) => (
              <List.Item key={i}>
                <Col span={24}>
                  <Row justify="space-between" align="middle" style={{ flexWrap: 'nowrap' }}>
                    <TimePicker
                      value={[hourToMoment(hour.start_hr), hourToMoment(hour.end_hr)]}
                      onChange={(selectedTimeRange) => updateCustomTime(selectedTimeRange, i)}
                    />
                    <WeekDayPicker
                      value={hour.days}
                      disabled={disabledDays}
                      onChange={(selectedDays) => updateCustomTimeDays(selectedDays, i)}
                      style={{ marginLeft: '16px' }}
                    />
                    <Button
                      type="link"
                      icon={{ name: "trash-2", strokeWidth: "1", color: "#F5222D", size: 20 }}
                      onClick={_ => removeCustomTime(i)}
                      disabled={!customHours || customHours.length <= 1}
                    />
                  </Row>
                </Col>
              </List.Item>
            )}
          />
          {!isFull ? (
            <>
              <Divider type="horizontal" style={{ marginTop: 0, marginBottom: '16px' }} />
              <Button
                type="default"
                icon={{ name: "plus", size: 14 }}
                style={{ marginBottom: '4px', marginLeft: '8px' }}
                onClick={addCustomTime}
              >
                Add custom time
              </Button>
            </>
          ) : null}
        </Col>
      </Row>
    </Col>
  );
};

export default ScheduleShift;
